/**
 * 用户引导工具
 * 从 guideConfig.ts 重构而来
 * 提供用户引导配置和管理功能
 * 
 * @author AI Assistant
 * @since 2025-01-12
 * @version 2.0.0
 */

// ============= 类型定义 =============

export interface GuideStep {
  element: string
  title: string
  content: string
  hint?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
}

export interface GuideStepsConfig {
  [path: string]: GuideStep[]
}

export interface StyleObject {
  top?: string
  left?: string
  width?: string
  height?: string
  borderRadius?: string
  transform?: string
  opacity?: string
  transition?: string
  position?: 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky'
  zIndex?: number
  display?: string
  visibility?: 'visible' | 'hidden' | 'collapse'
  pointerEvents?: 'auto' | 'none'
  backgroundColor?: string
  border?: string
  boxShadow?: string
  padding?: string
  margin?: string
  fontSize?: string
  fontWeight?: string | number
  color?: string
  textAlign?: 'left' | 'center' | 'right' | 'justify'
  lineHeight?: string | number
  maxWidth?: string
  minWidth?: string
  maxHeight?: string
  minHeight?: string
}

export interface GuideTheme {
  primaryColor: string
  backgroundColor: string
  textColor: string
  borderColor: string
  shadowColor: string
  highlightColor: string
  overlayColor: string
}

// ============= 引导步骤配置 =============

export const guideStepsConfig: GuideStepsConfig = {
  '/create': [
    {
      element: '.text-input-area',
      title: '📝 文本输入区域',
      content: '在这里输入您想要转换为语音的文本内容。支持纯文本和SSML格式，可以控制语速、停顿等细节。',
      hint: '💡 提示：免费用户每次最多500字符，注册用户最多1000字符'
    },
    {
      element: '.voice-selector',
      title: '🎤 主播选择',
      content: '选择您喜欢的主播。我们提供多种中文和英文主播供您选择，每个主播都有独特的音色和风格。',
      hint: '💡 提示：点击主播卡片上的试听按钮可以预览效果，点击收藏按钮可以快速访问常用主播'
    },
    {
      element: '.voice-selector',
      title: '⚙️ 音频设置',
      content: '点击任意主播卡片打开详情面板，即可调整语音的语速、音调和音量。让语音更符合您的需求。',
      hint: '💡 提示：建议先使用默认设置，生成后根据效果微调。语速1.0为正常速度，0.5为慢速，2.0为快速'
    },
    {
      element: '.convert-button',
      title: '🚀 开始转换',
      content: '点击此按钮开始将文本转换为语音。转换任务会在后台处理，您可以在任务中心查看进度。',
      hint: '💡 提示：转换时间取决于文本长度和当前队列情况，通常在几秒到几分钟之间'
    },
    {
      element: '.char-counter-billing',
      title: '📊 计费点数查看',
      content: '在这里查看当前输入的字符数和预计消耗的计费点数。不同语言和SSML标签的计费权重有所不同。',
      hint: '💡 提示：点击旁边的问号图标可以查看详细的计费规则说明',
      position: 'top'
    }
  ],
  '/batch-conversion': [
    {
      element: '.batch-input-methods',
      title: '📋 批量输入方式',
      content: '支持多种批量输入方式：手动输入、导入CSV/Excel、导入文本文件。适合批量生成多个音频文件。',
      hint: '💡 提示：CSV/Excel格式可以为每行指定不同的主播和参数'
    },
    {
      element: '.batch-table',
      title: '📊 批量任务表格',
      content: '在表格中编辑每个任务项的文本、主播、语速等参数。支持批量设置和单独调整。',
      hint: '💡 提示：可以使用Ctrl+C/V复制粘贴，支持批量填充'
    },
    {
      element: '.batch-submit',
      title: '🚀 提交批量任务',
      content: '点击提交后，所有任务项会被创建并在后台处理。您可以在任务中心查看进度。',
      hint: '💡 提示：提交后会自动跳转到任务中心'
    }
  ],
  '/conversion-records': [
    {
      element: '.task-status-tabs',
      title: '📑 任务状态分类',
      content: '任务按状态分为：进行中、已完成、失败。点击标签页可以快速筛选。',
      hint: '💡 提示：进行中的任务会实时更新进度'
    },
    {
      element: '.task-card',
      title: '📦 任务卡片',
      content: '每个任务卡片显示任务名称、类型、进度和状态。点击可以查看详情。',
      hint: '💡 提示：批量任务会显示子任务的完成情况'
    },
    {
      element: '.task-actions',
      title: '⚡ 任务操作',
      content: '支持查看详情、下载结果、重试失败任务、取消任务等操作。',
      hint: '💡 提示：失败的任务可以重试，会保留原有配置'
    }
  ],
  '/works': [
    {
      element: '.asset-filters',
      title: '🔍 资产筛选',
      content: '使用标签、收藏、日期等条件筛选资产。支持搜索文件名和内容。',
      hint: '💡 提示：可以创建自定义标签来组织资产'
    },
    {
      element: '.asset-grid',
      title: '🎵 资产列表',
      content: '查看所有生成的音频资产。支持网格和列表两种视图模式。',
      hint: '💡 提示：点击资产卡片可以查看详情、播放、下载'
    },
    {
      element: '.asset-actions',
      title: '⚡ 批量操作',
      content: '选中多个资产后，可以批量合并、导出、打标签、删除等。',
      hint: '💡 提示：合并功能可以将多个音频文件合并为一个'
    }
  ],
  '/user/entitlement': [
    {
      element: '.package-info',
      title: '📦 当前套餐',
      content: '查看您当前的套餐信息，包括额度、有效期、权益等。',
      hint: '💡 提示：套餐到期后会自动降级为免费用户'
    },
    {
      element: '.usage-stats',
      title: '📊 使用统计',
      content: '查看本月的使用情况，包括已用额度、剩余额度、使用趋势等。',
      hint: '💡 提示：额度每月重置，未用完的额度不会累积'
    },
    {
      element: '.usage-records',
      title: '📝 使用记录',
      content: '查看详细的使用记录，支持按日期、任务类型筛选。可以导出为Excel或CSV。',
      hint: '💡 提示：使用记录可以用于对账和分析'
    }
  ],
  '/user': [
    {
      element: '.user-menu',
      title: '👤 用户中心',
      content: '这里是您的个人中心，可以管理账号信息、套餐、收藏、推广等。',
      hint: '💡 提示：完善个人信息可以获得更好的服务体验'
    }
  ]
};

// ============= 默认主题配置 =============

export const defaultGuideTheme: GuideTheme = {
  primaryColor: '#409EFF',
  backgroundColor: '#ffffff',
  textColor: '#303133',
  borderColor: '#DCDFE6',
  shadowColor: 'rgba(0, 0, 0, 0.1)',
  highlightColor: '#409EFF',
  overlayColor: 'rgba(0, 0, 0, 0.5)'
};

// ============= 工具函数 =============

/**
 * 获取指定路径的引导步骤
 */
export function getGuideSteps(path: string): GuideStep[] {
  return guideStepsConfig[path] || [];
}

/**
 * 检查路径是否有引导步骤
 */
export function hasGuideSteps(path: string): boolean {
  return path in guideStepsConfig && guideStepsConfig[path].length > 0;
}

/**
 * 合并引导主题
 */
export function mergeGuideTheme(
  theme: Partial<GuideTheme> = {}
): GuideTheme {
  return { ...defaultGuideTheme, ...theme };
}

/**
 * 获取页面信息
 */
export function getPageInfo(path: string) {
  const pageMap: Record<string, { title: string; description: string }> = {
    '/create': { title: '文本转语音', description: '将文本转换为语音文件' },
    '/batch-conversion': { title: '批量处理', description: '批量转换多个文本文件' },
    '/conversion-records': { title: '转换记录', description: '查看历史转换记录' },
    '/works': { title: '作品库', description: '管理生成的音频资产' },
    '/user': { title: '用户中心', description: '管理个人信息和设置' },
    '/settings': { title: '系统设置', description: '配置应用参数' }
  };

  return pageMap[path] || { title: '未知页面', description: '' };
}

/**
 * 检查页面是否支持引导
 */
export function isPageSupportedForGuide(path: string): boolean {
  return Object.keys(guideStepsConfig).includes(path);
}

/**
 * 获取引导完成状态
 */
export function getGuideCompletedStatus(path: string): boolean {
  const key = `guide_completed_${path}`;
  return localStorage.getItem(key) === 'true';
}

/**
 * 设置引导完成状态
 */
export function setGuideCompletedStatus(path: string, completed: boolean): void {
  const key = `guide_completed_${path}`;
  localStorage.setItem(key, completed.toString());
}

/**
 * 重置所有引导状态
 */
export function resetAllGuideStatus(): void {
  Object.keys(guideStepsConfig).forEach(path => {
    setGuideCompletedStatus(path, false);
  });
}

/**
 * 获取引导跳过状态
 */
export function getGuideSkippedStatus(): boolean {
  return localStorage.getItem('guide_skipped') === 'true';
}

/**
 * 设置引导跳过状态
 */
export function setGuideSkippedStatus(skipped: boolean): void {
  localStorage.setItem('guide_skipped', skipped.toString());
}

// ============= 导出工具集合 =============

export const guideUtils = {
  // 配置
  guideStepsConfig,
  defaultGuideTheme,
  
  // 工具函数
  getGuideSteps,
  hasGuideSteps,
  mergeGuideTheme,
  getPageInfo,
  isPageSupportedForGuide,
  getGuideCompletedStatus,
  setGuideCompletedStatus,
  resetAllGuideStatus,
  getGuideSkippedStatus,
  setGuideSkippedStatus
};

export default guideUtils;
